import { CloudUploadOutlined, DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Select, Space, Tabs, Tag } from 'antd';
import * as Component from 'components';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SERVICE_DISTRIBUTION_OPTIONS } from '../../movie/constants';
import { ManualUploadShortsModal } from '../components';
import { shortsService } from '../services/shortsService';
import { ShortsListItem } from '../types/shortsType';
import './Shorts.css';

/** BEE-5023 (PRD v1.4 #19): VOD / LIVE / AD 탭. 'all' 은 필터 미적용. */
const SUBTYPE_TABS: { key: string; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'vod', label: 'VOD' },
  { key: 'live', label: 'LIVE' },
  { key: 'ad', label: 'AD' },
];

const SUBTYPE_COLOR: Record<string, string> = {
  vod: 'blue',
  live: 'magenta',
  ad: 'orange',
};

const Shorts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [serviceDistributionFilter, setServiceDistributionFilter] = useState<string[]>(
    searchParams.get('service_distribution')?.split(',') || []
  );
  const [manualUploadOpen, setManualUploadOpen] = useState(false);
  const subtypeFilter = (searchParams.get('subtype') as string) || 'all';

  const handleSubtypeChange = (key: string) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'all') {
      next.delete('subtype');
    } else {
      next.set('subtype', key);
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  const handleServiceDistributionChange = (values: string[]) => {
    setServiceDistributionFilter(values);
    const newParams = new URLSearchParams(searchParams);
    if (values.length > 0) {
      newParams.set('service_distribution', values.join(','));
    } else {
      newParams.delete('service_distribution');
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  // Get English poster thumbnails (landscape and portrait) — same as Movie list
  const getEnglishPosters = (thumbnails?: any[]) => {
    if (!thumbnails) return { landscape: null, portrait: null };
    const englishThumbnails = thumbnails.filter(
      t => t.language?.toUpperCase() === 'EN' || t.language?.toUpperCase() === 'ENGLISH'
    );
    return {
      landscape: englishThumbnails.find(t => t.orientation === 'landscape'),
      portrait: englishThumbnails.find(t => t.orientation === 'portrait'),
    };
  };

  const columns = [
    {
      title: 'POSTER',
      dataIndex: 'thumbnails',
      key: 'poster',
      width: '15%',
      render: (_text: string, record: ShortsListItem) => {
        const posters = getEnglishPosters(record.thumbnails);
        return (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {posters.landscape && (
              <img
                src={posters.landscape.file || ''}
                alt="landscape"
                style={{ width: '80px', height: '45px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {posters.portrait && (
              <img
                src={posters.portrait.file || ''}
                alt="portrait"
                style={{ width: '35px', height: '50px', objectFit: 'cover', borderRadius: '4px' }}
              />
            )}
            {!posters.landscape && !posters.portrait && (
              <div style={{ color: '#999', fontSize: '12px' }}>No poster</div>
            )}
          </div>
        );
      },
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
      sorter: true,
      render: (_text: string, record: ShortsListItem) => {
        const enTitle = record.i18n_title?.en || record.title_en || '';
        const koTitle = record.i18n_title?.ko || '';
        const zhTitle = record.i18n_title?.['zh-hans'] || '';
        return (
          <div
            onClick={() => navigate(`/episode/edit/${record.id}`)}
            style={{ cursor: 'pointer', color: '#1890ff' }}
          >
            {enTitle && <div style={{ fontWeight: 'bold' }}>{enTitle}</div>}
            {(koTitle || zhTitle) && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                {koTitle}
                {koTitle && zhTitle && ' / '}
                {zhTitle}
              </div>
            )}
            {!enTitle && !koTitle && !zhTitle && <div style={{ color: '#999' }}>Untitled</div>}
          </div>
        );
      },
    },
    {
      title: 'SLUG',
      dataIndex: 'slug',
      key: 'slug',
      width: '15%',
      sorter: true,
    },
    {
      title: 'SUBTYPE',
      dataIndex: 'shorts_meta',
      key: 'subtype',
      width: '10%',
      render: (_: any, record: ShortsListItem) => {
        const st = record.shorts_meta?.subtype ?? null;
        if (!st) return <span style={{ color: '#bbb' }}>-</span>;
        return <Tag color={SUBTYPE_COLOR[st.toLowerCase()] ?? 'default'}>{st.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'ACTIONS',
      dataIndex: 'id',
      key: 'actions',
      width: '10%',
      render: (_: any, record: ShortsListItem) => (
        <Space>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate(`/episode/edit/${record.id}`)}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              // TODO: Implement delete functionality (parity with Movie list)
              console.log('Delete shorts:', record.id);
            }}
          />
        </Space>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <div className="shorts-page">
        {/* BEE-5023 (PRD v1.4 #19): VOD/LIVE/AD 탭. */}
        <Tabs
          activeKey={subtypeFilter}
          onChange={handleSubtypeChange}
          items={SUBTYPE_TABS}
          style={{ marginBottom: 8 }}
        />

        {/* Filter Section */}
        <div
          style={{
            marginBottom: '16px',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '4px',
            border: '1px solid #d9d9d9',
          }}
        >
          <Space wrap size="middle">
            <div>
              <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                Service Distribution
              </div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="All Sources"
                value={serviceDistributionFilter}
                onChange={handleServiceDistributionChange}
                options={SERVICE_DISTRIBUTION_OPTIONS}
              />
            </div>
          </Space>
        </div>

        {/* Table */}
        <Component.TableComponent<ShortsListItem>
          name="Shorts"
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          additionalButton={[
            <Button
              key="create-shorts"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/episode/edit/new?content_type=shorts')}
              size="small"
            >
              Create Shorts
            </Button>,
            <Button
              key="manual-upload-shorts"
              icon={<CloudUploadOutlined />}
              onClick={() => setManualUploadOpen(true)}
              size="small"
            >
              Manual Upload
            </Button>,
          ]}
          columns={columns}
          dataFetch={async (params: URLSearchParams) => {
            // backend 가 subtype 필터를 못 받으면 client-side fallback.
            // subtype 은 URL searchParam 으로 이미 들어가 있어 fetchList 가 그대로 전달.
            const result = await shortsService.fetchList(params);
            const subtype = params.get('subtype');
            const filtered = subtype
              ? result.results.filter(
                  r => (r.shorts_meta?.subtype ?? '').toLowerCase() === subtype,
                )
              : result.results;
            return {
              data: filtered,
              count: subtype ? filtered.length : result.count,
            };
          }}
        />
        <ManualUploadShortsModal
          open={manualUploadOpen}
          onClose={() => setManualUploadOpen(false)}
          onCreated={shortsId => navigate(`/episode/edit/${shortsId}`)}
        />
      </div>
    </Component.BasePageComponent>
  );
};

export default Shorts;
